import { useEffect, useRef, useState } from "react";
import { Reveal } from "./Reveal";
import { cn } from "../../lib/utils";

const PILLARS = [
  {
    index: "01",
    title: "Software Development",
    summary:
      "Product and platform engineering — from architecture and interface design through to the code that carries it.",
    points: [
      "Web and application engineering",
      "System and API architecture",
      "Interface and interaction design",
      "Maintainable, reviewed codebases",
    ],
  },
  {
    index: "02",
    title: "Deployment & Delivery",
    summary:
      "Software becomes real once it reaches production. Delivery is treated as an engineered discipline in its own right.",
    points: [
      "CI/CD pipelines and automation",
      "Environment and release strategy",
      "Production readiness practices",
      "Repeatable, auditable deployments",
    ],
  },
  {
    index: "03",
    title: "Cloud Services",
    summary:
      "Infrastructure designed to stay predictable as systems grow — in cost, in performance and in operation.",
    points: [
      "Cloud architecture and provisioning",
      "Scalable, resilient foundations",
      "Observability and operations",
      "Efficiency-conscious design",
    ],
  },
];

/** Vertical schematic: three nodes on one trajectory, the active one lit. */
function Schematic({ active }: { active: number }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 120 420"
      className="h-[420px] w-[120px]"
      fill="none"
    >
      <path
        d="M 60 20 C 100 110, 20 200, 60 290 C 90 356, 70 386, 60 400"
        stroke="color-mix(in oklab, var(--foreground) 14%, transparent)"
        strokeWidth="1"
      />
      <path
        d="M 60 20 C 100 110, 20 200, 60 290 C 90 356, 70 386, 60 400"
        stroke="var(--primary)"
        strokeWidth="1.25"
        strokeDasharray="620"
        strokeDashoffset={620 - (active + 1) * 200}
        className="transition-[stroke-dashoffset] duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
      />
      {[20, 200, 386].map((cy, i) => (
        <g key={cy}>
          <circle
            cx={i === 1 ? 40 : 60}
            cy={cy}
            r="16"
            fill="var(--primary)"
            className={cn(
              "transition-opacity duration-700",
              i === active ? "opacity-[0.14]" : "opacity-0",
            )}
          />
          <circle
            cx={i === 1 ? 40 : 60}
            cy={cy}
            r="3.5"
            className={cn(
              "transition-all duration-700",
              i === active ? "fill-primary" : "fill-transparent",
            )}
            stroke="color-mix(in oklab, var(--foreground) 35%, transparent)"
            strokeWidth="1"
          />
        </g>
      ))}
    </svg>
  );
}

export function Expertise() {
  const [active, setActive] = useState(0);
  const rows = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const nodes = rows.current.filter((n): n is HTMLDivElement => Boolean(n));
    if (!nodes.length || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const i = Number((visible.target as HTMLElement).dataset["i"]);
          if (!Number.isNaN(i)) setActive(i);
        }
      },
      { rootMargin: "-35% 0px -45% 0px", threshold: [0, 0.3, 0.75] },
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="expertise"
      className="scroll-mt-24 relative overflow-hidden border-t border-hairline py-24 sm:py-28 lg:py-36"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 grid-field opacity-30"
        style={{ maskImage: "linear-gradient(to bottom, transparent, black 25%, black 70%, transparent)" }}
      />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-16">
          <Reveal className="flex items-center gap-4 self-start">
            <span className="h-px w-10 bg-primary" />
            <p className="eyebrow">Expertise</p>
          </Reveal>
          <div>
            <Reveal>
              <h2 className="font-display text-[clamp(1.9rem,4.4vw,3.4rem)] leading-[1.04]">
                Three disciplines
                <br />
                on one continuous system.
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground">
                Design, delivery and operation are not separate engagements. What is designed
                well is easier to deliver, and what is delivered well is far easier to run —
                so we keep all three on the same trajectory.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mt-20 grid gap-10 lg:grid-cols-[120px_minmax(0,1fr)] lg:gap-16">
          <div className="hidden lg:block">
            <div className="sticky top-32">
              <Schematic active={active} />
            </div>
          </div>

          <div>
            {PILLARS.map((pillar, i) => (
              <div
                key={pillar.index}
                data-i={i}
                ref={(el) => {
                  rows.current[i] = el;
                }}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  "group relative grid gap-8 border-t border-hairline py-12 transition-colors duration-700 sm:py-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:gap-16",
                  i === PILLARS.length - 1 && "border-b",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute left-0 top-0 h-px w-full origin-left bg-primary transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
                    i === active ? "scale-x-100" : "scale-x-0",
                  )}
                />
                <Reveal>
                  <div className="flex items-start gap-6">
                    <span
                      className={cn(
                        "num text-[clamp(2.5rem,6vw,4.5rem)] leading-none transition-colors duration-700",
                        i === active ? "text-primary" : "outline-figure",
                      )}
                    >
                      {pillar.index}
                    </span>
                    <div className="mt-2">
                      <h3 className="max-w-[12ch] font-display text-[clamp(1.5rem,3vw,2.15rem)] leading-[1.08] tracking-[-0.02em]">
                        {pillar.title}
                      </h3>
                      <p className="mt-6 flex items-center gap-3 text-[0.65rem] tracked text-muted-foreground/70">
                        <span aria-hidden="true" className="h-px w-6 bg-primary/40" />
                        {i === PILLARS.length - 1
                          ? "Feeds back into Software Development"
                          : `Feeds into ${PILLARS[i + 1].title}`}
                      </p>
                    </div>
                  </div>
                </Reveal>

                <Reveal delay={90} className="lg:pt-3">
                  <p className="max-w-md text-[0.95rem] leading-relaxed text-muted-foreground">
                    {pillar.summary}
                  </p>
                  <ul className="mt-8 max-w-md">
                    {pillar.points.map((point) => (
                      <li
                        key={point}
                        className="flex items-center gap-4 border-t border-hairline py-3 text-[0.875rem] text-muted-foreground transition-colors duration-500 hover:text-foreground"
                      >
                        <span
                          aria-hidden="true"
                          className="h-px w-4 shrink-0 bg-primary/50 transition-all duration-500 group-hover:w-6"
                        />
                        {point}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
